// Per-wallet settings — local-first, carried on the profile (v0.80.40).
//
// The rule that shapes this (decisions-log): **public identity and preferences
// may live on the wallet; anything that names another person may not.** So
// theme, emoji, pockets and "don't show again" sync; `m.nick`, `m.nickAlso`
// and `m.recent` never do — they stay on the device, as decided in v0.70.12.
//
// Local storage is the source of truth for rendering: the app reads it at boot
// and paints from it, so a returning browser behaves exactly as before. The
// profile request the app ALREADY makes at login (main.ts) carries the wallet's
// settings back; we merge per key, newest wins, and push only if we're ahead.
// No extra round trip, no key ceremony — these aren't secrets.
//
// A new browser or a fresh Home Screen install has empty local storage, so the
// server's copy simply lands and is applied. That is the whole point.

/** A value plus when it was set, so two browsers can merge without a server. */
interface Stamped {
  v: unknown;
  at: number;
}
type Bag = Record<string, Stamped>;

/** The keys that sync, and the localStorage key each mirrors. */
export const SYNCED: Record<string, string> = {
  theme: "m.theme",
  emoji: "m.emoji",
  currencies: "m.currencies",
  currency: "m.currency",
  hintDrop: "m.hintDrop2",
  mask: "m.maskBalance", // 1.4.67 — "Mask my balance" (More): the pill rests on •••• only when this is "1"
  // 1.5.50 (Alexander, 31 Oct: "Go.") — the M® card's PUBLIC record (credential id, public key, rpId, transports, kind;
  // card.ts) and the limit $X are the wallet's, not one browser's: Chrome, a Private tab or a second device paid above
  // the limit with the passkey alone because only the enrolling browser knew the card. Nothing here is secret. A removed
  // card is "" (a tombstone with a newer stamp), so the wallet's copy cannot bring it back. The rule's real home is the
  // account's module on Arc (security-architecture.md); this makes every honest client behave as one until then.
  card: "m.card",
  cardAbove: "m.cardAbove",
};

// 1.4.67 (Alexander, 21 Oct) — the balance pill rests open; "Mask my balance" in More turns the bullets on.
// A preference of the wallet's, so it syncs like the theme (the rule above: preferences may live on the wallet).
export const MASK_KEY = "m.maskBalance";
export function maskBalance(): boolean {
  try { return localStorage.getItem(MASK_KEY) === "1"; } catch { return false; }
}
export function setMaskBalance(on: boolean): void {
  try {
    localStorage.setItem(MASK_KEY, on ? "1" : "0");
    touch("mask");
  } catch { /* the pill follows what's stored; nothing else changes */ }
}

const MIRROR = "m.settings"; // our own stamps, alongside the mirrored values

function readBag(): Bag {
  try {
    const raw = localStorage.getItem(MIRROR);
    if (raw) return JSON.parse(raw) as Bag;
  } catch {
    /* fall through */
  }
  return {};
}

function writeBag(b: Bag): void {
  try {
    localStorage.setItem(MIRROR, JSON.stringify(b));
  } catch {
    /* non-fatal */
  }
}

/** Snapshot of the live localStorage values, stamped from our own bag. */
function localBag(): Bag {
  const stamps = readBag();
  const out: Bag = {};
  for (const [key, lsKey] of Object.entries(SYNCED)) {
    let v: unknown = null;
    try {
      v = localStorage.getItem(lsKey);
    } catch {
      /* ignore */
    }
    if (v === null) continue; // nothing set here yet
    out[key] = { v, at: stamps[key]?.at ?? 0 };
  }
  return out;
}

/** Newest wins, per key. */
function merge(a: Bag, b: Bag): Bag {
  const out: Bag = { ...a };
  for (const [k, e] of Object.entries(b)) {
    if (!out[k] || e.at > out[k].at) out[k] = e;
  }
  return out;
}

/** Write a merged bag into localStorage (values) and our stamps. */
function apply(bag: Bag): string[] {
  const changed: string[] = [];
  for (const [key, lsKey] of Object.entries(SYNCED)) {
    const e = bag[key];
    if (!e || typeof e.v !== "string") continue;
    let cur: string | null = null;
    try {
      cur = localStorage.getItem(lsKey);
    } catch {
      /* ignore */
    }
    if (cur === e.v) continue;
    try {
      localStorage.setItem(lsKey, e.v);
      changed.push(key);
    } catch {
      /* non-fatal */
    }
  }
  writeBag(bag);
  return changed;
}

// v0.80.41 — pushing on change, not just at the next login.
//
// Until now a stamped change waited for THIS browser's next login to reach the
// wallet, so "change it in Chrome, open Safari" read the older server copy —
// correctly by the newest-wins rule, just from stale data. The address is
// registered once after login; every stamp then schedules a debounced push,
// and anything still pending is flushed when the page is hidden (iOS kills
// backgrounded tabs freely).
let pushAddress: string | null = null;
let pushTimer: number | undefined;
let pushBound = false;
/** 1.5.58 — a change stamped before any home bound the pusher (the M® card's record, saved at boot on the return from the
 *  approver's enrolment): pushed the moment the home binds, so the wallet's copy is the new card's, not only at a later change. */
let pushOwed = false;

export function setPusher(address: string): void {
  pushAddress = address;
  if (typeof document !== "undefined" && !pushBound) {
    pushBound = true;
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flush();
    });
    window.addEventListener("pagehide", flush);
  }
  if (pushOwed && pushTimer === undefined) { pushOwed = false; pushTimer = window.setTimeout(flush, 1200); }
}

/** v1.1.6 — forget the wallet pushes go to (a wallet switch): the next home
 *  mount binds the new one. Anything pending belonged to the old wallet's
 *  values, which have just been put aside — dropped, not sent. */
export function releasePusher(): void {
  window.clearTimeout(pushTimer);
  pushTimer = undefined;
  pushAddress = null;
  pushOwed = false;
}

function flush(): void {
  window.clearTimeout(pushTimer);
  pushTimer = undefined;
  if (!pushAddress) { pushOwed = true; return; }
  const body = JSON.stringify(readBag());
  // Fire-and-forget; a failed push is retried by the next change or login.
  void import("./api").then((api) =>
    api.setProfile({ address: pushAddress!, settings: body }).catch(() => {}),
  );
}

/** Call whenever a synced value changes locally, so it wins the next merge. */
export function touch(key: keyof typeof SYNCED | string): void {
  const bag = readBag();
  let v: unknown = null;
  try {
    v = localStorage.getItem(SYNCED[key] ?? "");
  } catch {
    /* ignore */
  }
  bag[key] = { v, at: Date.now() };
  writeBag(bag);
  // Debounced: a settings sheet can change several keys in a row.
  window.clearTimeout(pushTimer);
  pushTimer = window.setTimeout(flush, 1200);
}

/**
 * Reconcile with the wallet's stored settings.
 * @returns the JSON to push back when this device is ahead, else null, and the
 *          list of keys that changed locally (so the caller can re-apply them).
 */
export function reconcile(remote: string | null | undefined): {
  push: string | null;
  changed: string[];
} {
  let theirs: Bag = {};
  if (remote) {
    try {
      theirs = JSON.parse(remote) as Bag;
    } catch {
      theirs = {};
    }
  }
  const mine = localBag();
  const merged = merge(theirs, mine);
  const changed = apply(merged);
  // Push only when we actually add something they don't have.
  const ahead = JSON.stringify(merged) !== JSON.stringify(theirs);
  return { push: ahead ? JSON.stringify(merged) : null, changed };
}
